import { useCallback, useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router'
import { AppointmentCancellationReason, EventType, RouteParameters, ToastSubType } from '../../../../constants'
import { Route } from '../../../../constants/route'
import { useAppointment, useCountry, useEventTracking, useFormAnalytics, useNotification, useTrackError } from '../../../../hooks'
import { useAppointmentsService } from '../../../../hooks/services'
import { AppointmentCancelRequest, AppointmentCancellationFormData, CancellationFormFailedEventData } from '../../../../types'
import { getDeliveryModelFromJourney, hydratePathname, logFactory } from '../../../../util'
import { Form as SharedForm } from '../../../forms'
import { handleSubmissionFailure } from '../../util'
import { PrimaryAppointmentCancellationReason } from '../utils'
import { Content } from './Content'
import { validationSchema } from './validationSchema'

interface Props {
  defaultValues: AppointmentCancellationFormData
  isReadOnly?: boolean
}

const log = logFactory('Form')
const cancelErrorMessage = 'Sorry, there was an error while cancelling this appointment'

const Form = ({ defaultValues }: Props) => {
  const { cancelAppointment } = useAppointmentsService()
  const { isLoading: isAppointmentLoading, appointment } = useAppointment()
  const { [RouteParameters.APPOINTMENT_ID]: appointmentId } = useParams()
  const country = useCountry()
  const navigate = useNavigate()
  const { track: trackEvent } = useEventTracking()
  const { notify } = useNotification()
  const trackError = useTrackError()
  const { onErrorsChange, readSummary } = useFormAnalytics<AppointmentCancellationFormData>()

  useEffect(() => {
    trackEvent(EventType.APPOINTMENT_FORM_VIEWED)
  }, [])

  const deliveryModel = useMemo(() => getDeliveryModelFromJourney(appointment?.journey), [appointment?.journey])
  const schema = useMemo(() => validationSchema(country, deliveryModel), [country, deliveryModel])

  const onError = useCallback((errors) => onErrorsChange(errors), [onErrorsChange])

  const onSubmit = useCallback(
    async (data: AppointmentCancellationFormData): Promise<void> => {
      const primaryReason = data.reason === PrimaryAppointmentCancellationReason.TECH_SETUP_FAILED ? data.techSetupFailReason : data.reason

      // cast to AppointmentCancellationReason, because it will come in as PrimaryAppointmentCancellationReason, but always be used as this.
      const reason = primaryReason as AppointmentCancellationReason
      const request: AppointmentCancelRequest = {
        appointmentId: appointmentId as string,
        description: data.description ? (data.description as string) : undefined,
        reason,
      }

      const { didError, totalClicks, totalSessionDurationInSeconds, submitCount } = readSummary()

      const eventData: CancellationFormFailedEventData = {
        appointmentId: request.appointmentId,
        description: request.description,
        didError,
        formType: 'cancellation',
        reason: request.reason,
        submissionCount: submitCount + 1, // We only increment submission on count when validation fails, so we need to bump one for passing validation
        totalClicks,
        totalSessionDurationInSeconds,
      }

      log('📩 Submitting appointment cancellation request...', request)

      try {
        await cancelAppointment(request)

        trackEvent(EventType.APPOINTMENT_CANCELLATION_FORM_SUBMISSION_SUCCEEDED, { data: eventData })

        const pathname = hydratePathname(Route.APPOINTMENT_CANCELLATION_SUCCESS, {
          [RouteParameters.COUNTRY]: country,
          [RouteParameters.APPOINTMENT_ID]: appointmentId,
        })

        navigate(pathname, { replace: true })
      } catch (error: any) {
        const failedEventData = {
          data: { ...eventData, didError: true },
          type: EventType.APPOINTMENT_CANCELLATION_FORM_SUBMISSION_FAILED,
        }

        handleSubmissionFailure({
          error,
          eventData: failedEventData,
          fallbackErrorMessage: cancelErrorMessage,
          notify,
          toastSubType: ToastSubType.CANCELLATION_FAILURE,
          trackError,
          trackEvent,
        })

        // Rethrow so our form's isSubmitSuccessful isn't set to true
        throw error
      }
    },
    [appointmentId, appointment?.country, country, cancelAppointment, navigate, notify, readSummary, trackError, trackEvent]
  )

  return (
    <SharedForm<AppointmentCancellationFormData>
      key={String(deliveryModel)}
      defaultValues={defaultValues}
      disabled={isAppointmentLoading}
      onError={onError}
      onSubmit={onSubmit}
      validationSchema={schema}
    >
      <Content deliveryModel={deliveryModel} />
    </SharedForm>
  )
}

export { Form }
